import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import './CustomModal.css';
import { generarExcel, obtenerDetallesLog, actualizarSerieFilterJson } from 'services/series';
import DynamicTable from 'utilities/DynamicTable';
import { createColumnHelper } from '@tanstack/react-table';


const columnHelper = createColumnHelper();

// Extensiones permitidas para el archivo de actualización.
const EXTENSIONES_EXCEL = ['.xlsx', '.xls'];

export default function ModalSerie({
    modal,
    toggle,
    Id,
    token
}) {
    const [detalles, setDetalles] = useState([]);

    // Estado del modal de actualización (subir Excel).
    const [modalUpdate, setModalUpdate] = useState(false);
    const [filaSeleccionada, setFilaSeleccionada] = useState(null);
    const [archivo, setArchivo] = useState(null);
    const [error, setError] = useState('');
    const [subiendo, setSubiendo] = useState(false);

    const fetchDetalles = async ()=>{
        const response = await obtenerDetallesLog(token, Id);
        response.forEach((item)=>{
            // eslint-disable-next-line no-useless-escape
            item.nombre = item.nombre_archivo.replace(/[\[\]]/g, '').replace('.json', '');
        });
        setDetalles(response);
    }

    useEffect(()=>{
        if(modal){
            fetchDetalles();
        }
    },[Id, modal])


    const handleViewModal = async(data) => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0'); // Asegura que el mes tenga 2 dígitos
        const day = String(today.getDate()).padStart(2, '0'); // Asegura que el día tenga 2 dígitos
        const hours = String(today.getHours()).padStart(2, '0'); // Asegura que la hora tenga 2 dígitos
        const minutes = String(today.getMinutes()).padStart(2, '0'); // Asegura que los minutos tengan 2 dígitos
        const seconds = String(today.getSeconds()).padStart(2, '0'); // Asegura que los segundos tengan 2 dígitos
        const formattedDate = `${day}/${month}/${year}_${hours}-${minutes}-${seconds}`;

        const result = await generarExcel(token, data.nombre_archivo, data.nombre, Id);

        const blob = await result.blob();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${data.nombre}_[${formattedDate}].xlsx`;
        link.click();
    };

    // Abre el modal de actualización guardando la fila (los datos no se pierden).
    const handleViewModalUpdate = (data) => {
        setFilaSeleccionada(data);
        setArchivo(null);
        setError('');
        setModalUpdate(true);
    };

    // Cierra el modal de actualización y limpia su estado.
    const cerrarModalUpdate = () => {
        if (subiendo) return;
        setModalUpdate(false);
        setFilaSeleccionada(null);
        setArchivo(null);
        setError('');
    };

    // Validación: un solo archivo y que sea Excel.
    const handleArchivoChange = (e) => {
        setError('');
        const seleccionado = e.target.files?.[0];
        if (!seleccionado) {
            setArchivo(null);
            return;
        }

        const nombre = seleccionado.name.toLowerCase();
        const esExcel = EXTENSIONES_EXCEL.some((ext) => nombre.endsWith(ext));
        if (!esExcel) {
            setArchivo(null);
            e.target.value = '';
            setError('El archivo debe ser un Excel (.xlsx o .xls).');
            return;
        }

        setArchivo(seleccionado);
    };

    const handleSubir = async () => {
        if (!archivo || !filaSeleccionada) return;

        setSubiendo(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('archivo', archivo);
            formData.append('nombreArchivo', filaSeleccionada.nombre_archivo);
            formData.append('idCarga', Id);

            for (const [k, v] of formData.entries()) console.log(k, v);

            // TODO: reemplazar por la llamada real al servicio de actualización.
            const result = await actualizarSerieFilterJson(token, formData);
            console.log(result);

            await fetchDetalles();
            setModalUpdate(false);
            setFilaSeleccionada(null);
            setArchivo(null);
        } catch (err) {
            setError('Ocurrió un error al subir el archivo. Inténtalo de nuevo.');
        } finally {
            setSubiendo(false);
        }
    };

    const columns = [
        columnHelper.accessor('nombre',{
          cell: (info)=> info.getValue(),
          header: ()=>(
            <span className="flex align-items-center">
              Nombre del Archivo
            </span>
          ),
        }),
        columnHelper.accessor('total',{
          cell: (info)=> info.getValue(),
          header: ()=>(
            <span className="flex align-items-center">
              Total de registros insertados
            </span>
          ),
          enableGlobalFilter: false,
        }),
        columnHelper.accessor('cantidad_archivos',{
          cell: (info)=> {
            return (
              <>
                <button
                className="btn btn-secondary btn-sm"
                onClick={() => handleViewModal(info.row.original)}
                style={{
                  backgroundColor: "#1D6F42",
                  borderColor: "#1D6F42",
                  color: "#fff",
                  marginRight: 3,
                }}
                >
                  Excel
              </button>
              <button
                className="btn btn-info btn-sm"
                onClick={() => handleViewModalUpdate(info.row.original)}
              >
                Actualizar
              </button>
              </>
            )
          },
          header: ()=>(
            <span className="flex align-items-center">
              Opciones
            </span>
          ),
          enableGlobalFilter: false,
        })
    ];

    return <>
        <Modal isOpen={modal} toggle={toggle} backdrop="static" keyboard={false}>
            <div className='text-center'>
                <ModalHeader toggle={toggle}>Detalles de archivos con nuevos registros</ModalHeader>
                <ModalBody>
                    <div className="row">
                        <div className="col-md-12">
                            <DynamicTable columns={columns} data={detalles} />
                        </div>
                    </div>
                </ModalBody>
            </div>
        </Modal>

        <Modal
            isOpen={modalUpdate}
            toggle={cerrarModalUpdate}
            className="modal-actualizar"
            centered
            backdrop="static"
            keyboard={false}
        >
            <ModalHeader toggle={cerrarModalUpdate}>Actualizar archivo</ModalHeader>
            <ModalBody>
                {filaSeleccionada && (
                    <p className="text-muted mb-3">
                        Registro: <strong>{filaSeleccionada.nombre}</strong>
                    </p>
                )}

                {!archivo ? (
                    <label htmlFor="archivo-update" className="dropzone mb-1">
                        <span style={{ fontSize: 22 }}>📄</span>
                        <span>Haz clic para seleccionar un archivo</span>
                        <small>Formato Excel (.xlsx o .xls) · un solo archivo</small>
                    </label>
                ) : (
                    <div className="archivo-chip mb-1">
                        <span className="text-truncate">📄&nbsp;{archivo.name}</span>
                        <button
                            type="button"
                            title="Quitar archivo"
                            onClick={() => {
                                setArchivo(null);
                                setError('');
                                const input = document.getElementById('archivo-update');
                                if (input) input.value = '';
                            }}
                            disabled={subiendo}
                        >
                            &times;
                        </button>
                    </div>
                )}

                <input
                    id="archivo-update"
                    type="file"
                    className="d-none"
                    accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    onChange={handleArchivoChange}
                    disabled={subiendo}
                />

                {error && (
                    <div className="text-danger mt-2 small">{error}</div>
                )}
            </ModalBody>
            <ModalFooter>
                <button
                    className="btn btn-secondary btn-sm"
                    onClick={cerrarModalUpdate}
                    disabled={subiendo}
                >
                    Cancelar
                </button>
                <button
                    className="btn btn-info btn-sm"
                    onClick={handleSubir}
                    disabled={!archivo || subiendo}
                >
                    {subiendo ? 'Subiendo...' : 'Subir'}
                </button>
            </ModalFooter>
        </Modal>
    </>
}
